import type * as React from "react";

import { ResizableTh } from "@/components/shared/resizable-th";
import { Skeleton } from "@/components/ui/skeleton";
import { type ResizableColumn, useResizableColumns } from "@/hooks/use-resizable-columns";
import { cn } from "@/lib/utils";

const DEFAULT_COLUMN_WIDTH = 160;

interface DataTableHeader {
  key: string;
  label: string;
  className?: string;
  defaultWidth?: number;
  minWidth?: number;
}

interface DataTableProps {
  id: string;
  headers: DataTableHeader[];
  children: React.ReactNode;
  loading?: boolean;
  empty?: React.ReactNode;
  colSpan?: number;
  className?: string;
}

export function DataTable({
  id,
  headers,
  children,
  loading = false,
  empty,
  colSpan,
  className,
}: DataTableProps) {
  const span = colSpan ?? headers.length;
  const hasChildren = Array.isArray(children)
    ? children.filter(Boolean).length > 0
    : Boolean(children);
  const showEmpty = !loading && !hasChildren && empty;

  const columns: ResizableColumn[] = headers.map((h) => ({
    key: h.key,
    defaultWidth: h.defaultWidth ?? DEFAULT_COLUMN_WIDTH,
    minWidth: h.minWidth,
  }));
  const { widths, startResize } = useResizableColumns(id, columns);

  return (
    <div className={cn("overflow-x-auto rounded-lg border", className)}>
      <table className="w-full table-fixed text-sm">
        <thead>
          <tr className="border-b bg-muted/50">
            {headers.map((header) => (
              <ResizableTh
                key={header.key}
                width={widths[header.key]}
                onResizeStart={startResize(header.key)}
                className={cn("text-xs", header.className)}
              >
                {header.label}
              </ResizableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading ? (
            Array.from({ length: 3 }).map((_, rowIndex) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton rows never reorder
              <tr key={`skeleton-${rowIndex}`} className="border-b last:border-b-0">
                {headers.map((_header, colIndex) => (
                  <td
                    // biome-ignore lint/suspicious/noArrayIndexKey: static skeleton cells never reorder
                    key={`skeleton-${rowIndex}-${colIndex}`}
                    className="px-4 py-3"
                  >
                    <Skeleton className="h-4 w-full" />
                  </td>
                ))}
              </tr>
            ))
          ) : showEmpty ? (
            <tr>
              <td colSpan={span}>{empty}</td>
            </tr>
          ) : (
            children
          )}
        </tbody>
      </table>
    </div>
  );
}
